import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { msmartAxios } from "../../../api/axios";
import { Link, useParams, NavLink } from "react-router-dom";
import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "react-flatpickr";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Select from "react-select";
import { useToast } from "../../../context/ToastContext";
import SummaryCharts, { RangePresets, SummaryNavItems, useSummaryTab } from "../components/summary/SummaryCharts";
import useTeamAccess from "../hooks/useTeamAccess";
import { SummaryBodySkeleton } from "../components/MsmartSkeletons";
import ErrorState from "../../../components/ErrorState";
import { getErrorMessage } from "../../../utils/errorMessage";


// =========================
// UI helpers
// =========================
function SectionCard({ title, right, children }) {
  return (
    <div className="card p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fw-bold">{title}</div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function MetricRow({ label, value, suffix = "", tone = "text-dark" }) {
  return (
    <div className="d-flex justify-content-between align-items-center py-2 border-top">
      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
        {label}
      </div>
      <div className={`fw-bold ${tone}`}>
        {value}
        {suffix}
      </div>
    </div>
  );
}

function KpiCard({ icon, title, value, tone = "text-dark", sub, onClick }) {
  return (
    <button
      type="button"
      className="card ss-kpi text-center p-3 h-100 w-100"
      onClick={onClick}
      style={{ textAlign: "inherit" }}
    >
      <div className="d-flex justify-content-center mb-2">
        <div className="ss-kpi-icon">
          <i className={`bi ${icon}`} style={{ fontSize: "1.4rem" }} />
        </div>
      </div>

      <div className={`fw-bold ${tone}`} style={{ fontSize: "1.25rem" }}>
        {value}
      </div>

      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
        {title}
      </div>

      {sub ? (
        <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
          {sub}
        </div>
      ) : null}
    </button>
  );
}

// =========================
// Helpers
// =========================
function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYMD(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function money(n) {
  const num = Number(n || 0);
  return `RM ${num.toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getDateRangeList(start, end) {
  const result = [];
  const current = new Date(start);
  const last = new Date(end);

  while (current <= last) {
    result.push(current.toLocaleDateString("sv-SE")); // YYYY-MM-DD
    current.setDate(current.getDate() + 1);
  }
  return result;
}

function calcTopDays(countByDate = {}, topN = 3) {
  return Object.entries(countByDate || {})
    .map(([d, v]) => [d, Number(v || 0)])
    .filter((x) => x[1] > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN);
}

// Create a clean default shape
const defaultSummaryShape = {
  totalLeadsAdded: { total: 0, countByDate: {} },
  totalPresentations: { total: 0, countByDate: {} },
  followUpScheduled: { total: 0, countByDate: {} },
  finalStatus: {
    Booking: { total: 0, countByDate: {} },
    Closed: { total: 0, countByDate: {} },
    Rejected: { total: 0, countByDate: {} },
    "Follow Up": { total: 0, countByDate: {} },
  },
  avgFollowUpPerLead: { total: 0, countByUser: {} },
  followUpPerCloseRatio: { total: 0, countByUser: {} },
  followUpToClosedRate: {
    total: 0,
    totalFollowUpLeads: 0,
    totalClosedLeads: 0,
    countByUser: {},
  },
  followUpToRejectedRate: {
    total: 0,
    totalFollowUpLeads: 0,
    totalRejectedLeads: 0,
    countByUser: {},
  },
  followUpToBookingRate: {
    total: 0,
    totalFollowUpLeads: 0,
    totalBookingLeads: 0,
    countByUser: {},
  },
  bookingToClosedRate: {
    total: 0,
    totalBookingLeads: 0,
    totalClosedFromBooking: 0,
    countByUser: {},
  },
  bookingToRejectedRate: {
    total: 0,
    totalBookingLeads: 0,
    totalRejectedFromBooking: 0,
    countByUser: {},
  },
  salesGapSummary: {
    targetAmount: 0,
    actualSalesAmount: 0,
    salesGap: 0,
    percentageAchieved: 0,
    countByDate: {},
  },
};

const defaultIndividualShape = {
  totalLeadsAdded: { total: 0, countByDate: {} },
  totalPresentations: { total: 0, countByDate: {} },
  followUpScheduled: { total: 0, countByDate: {} },
  finalStatus: {
    Booking: { total: 0, countByDate: {} },
    Closed: { total: 0, countByDate: {} },
    Rejected: { total: 0, countByDate: {} },
    "Follow Up": { total: 0, countByDate: {} },
  },
  avgFollowUpPerLead: { total: 0 },
  followUpPerCloseRatio: { total: 0 },
  followUpToClosedRate: { total: 0, totalFollowUpLeads: 0, totalClosedLeads: 0 },
  followUpToRejectedRate: { total: 0, totalFollowUpLeads: 0, totalRejectedLeads: 0 },
  followUpToBookingRate: { total: 0, totalFollowUpLeads: 0, totalBookingLeads: 0 },
  bookingToClosedRate: { total: 0, totalBookingLeads: 0, totalClosedFromBooking: 0 },
  bookingToRejectedRate: { total: 0, totalBookingLeads: 0, totalRejectedFromBooking: 0 },
  salesGapSummary: {
    targetAmount: 0,
    actualSalesAmount: 0,
    salesGap: 0,
    percentageAchieved: 0,
    countByDate: {},
  },
};

export default function TeamStatSummary() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const { notifyError } = useToast();
  const [tab, setTab] = useSummaryTab(); // "summary" | "analysis" (?view=analysis)

  const [summary, setSummary] = useState(defaultSummaryShape);
  const [individualSummary, setIndividualSummary] = useState(defaultIndividualShape);

  const [loading, setLoading] = useState(true);

  const [section, setSection] = useState("Team"); // "Team" | "Individual"
  const [members, setMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // { username, label }

  // KPI detail modal
  const [kpiModal, setKpiModal] = useState({
    open: false,
    title: "",
    subtitle: "",
    tone: "text-dark",
    icon: "bi-graph-up",
    total: 0,
    extraRows: [],
    countByDate: null,
    isMoney: false,
  });

  const openKpiModal = (payload) => setKpiModal({ open: true, ...payload });
  const closeKpiModal = () => setKpiModal((p) => ({ ...p, open: false }));

  const today = new Date();

  // ✅ Draft can default today, but Applied should start empty (avoid auto-fetch on mount)
  const [draftRange, setDraftRange] = useState([today, today]);
  const [appliedRange, setAppliedRange] = useState([today, today]);

  // Abort + dedupe refs (✅ separate refs to avoid collision)
  const abortRef = useRef(null);
  const lastTeamFetchKeyRef = useRef("");
  const lastIndFetchKeyRef = useRef("");

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Safe values for UI computations (series/table) even when range not applied
  const safeStart = useMemo(() => appliedRange?.[0] || draftRange?.[0] || new Date(), [appliedRange, draftRange]);
  const safeEnd = useMemo(() => appliedRange?.[1] || appliedRange?.[0] || draftRange?.[1] || draftRange?.[0] || new Date(), [
    appliedRange,
    draftRange,
  ]);

  const startDate = useMemo(() => toYMD(safeStart), [safeStart]);
  const endDate = useMemo(() => toYMD(safeEnd), [safeEnd]);

  const daysInRange = useMemo(() => {
    const a = new Date(safeStart);
    const b = new Date(safeEnd);
    const diff = Math.floor((b - a) / (1000 * 60 * 60 * 24)) + 1;
    return diff <= 0 ? 1 : diff;
  }, [safeStart, safeEnd]);

  const rangeLabel = useMemo(() => {
    const a = `${safeStart}`.slice(4, 15);
    const b = `${safeEnd}`.slice(4, 15);
    return `${a} to ${b}`;
  }, [safeStart, safeEnd]);

  const isRangeComplete = useMemo(() => {
    return !!appliedRange?.[0] && !!appliedRange?.[1];
  }, [appliedRange]);

  // =====================
  // Gate: ensure user belongs to team
  // =====================
  const access = useTeamAccess(teamId);
  const view = access.view;

  // =====================
  // Fetch members (for Individual select)
  // =====================
  useEffect(() => {
    if (!user?.token) return;
    setLoading(true);
    msmartAxios
      .get(`/api/msmart/manager/get/team/member/${teamId}`, { headers: { accessToken: user.token } })
      .then((res) => {
        if (res.data?.team) setMembers(res.data.team);
      })
      .catch((err) => notifyError(getErrorMessage(err, "Error fetching team members")))
      .finally(() => setLoading(false));
  }, [user?.token, teamId, notifyError]);

  // =====================
  // Member options grouped by managerUsername
  // =====================
  const groupedOptions = useMemo(() => {
    const nameMap = {};
    members.forEach((m) => {
      nameMap[m.username] = m.nameInTeam;
    });

    const groupedByManager = members.reduce((acc, m) => {
      const manager = m.managerUsername;
      if (!acc[manager]) acc[manager] = [];
      acc[manager].push({ value: m.username, label: m.nameInTeam });
      return acc;
    }, {});

    return Object.entries(groupedByManager)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([managerUsername, options]) => ({
        label: nameMap[managerUsername] || managerUsername,
        options,
      }));
  }, [members]);

  // username -> display name (charts & leaderboard)
  const memberName = useMemo(() => Object.fromEntries(members.map((m) => [m.username, m.nameInTeam])), [members]);

  // Leaderboard click: jump to that member's individual summary
  const openMember = useCallback((username) => {
    setSelectedUser({ username, label: memberName[username] || username });
    setSection("Individual");
  }, [memberName]);

  // =====================
  // Fetchers (✅ dedupe only here)
  // =====================
  const fetchTeamData = useCallback(async () => {
    const key = `team|${teamId}|${startDate}|${endDate}`;
    if (lastTeamFetchKeyRef.current === key) return;
    lastTeamFetchKeyRef.current = key;

    setLoading(true);
    try {
      const res = await msmartAxios.post(
        `/api/msmart/stats/manager/${teamId}`,
        { startDate, endOfDate: endDate },
        { headers: { accessToken: user.token } }
      );
      setSummary(res.data || defaultSummaryShape);
    } catch (err) {
      notifyError(getErrorMessage(err, "Unable to fetch team data."));
    } finally {
      setLoading(false);
    }
  }, [teamId, startDate, endDate, user.token, notifyError]);

  const fetchIndividualData = useCallback(
    async (username) => {
      const key = `individual|${teamId}|${username}|${startDate}|${endDate}`;
      if (lastIndFetchKeyRef.current === key) return;
      lastIndFetchKeyRef.current = key;

      // cancel previous request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const res = await msmartAxios.post(
          `/api/msmart/stats/manager/individual/${teamId}`,
          { startDate, endOfDate: endDate, username },
          { headers: { accessToken: user.token }, signal: controller.signal }
        );
        setIndividualSummary(res.data || defaultIndividualShape);
      } catch (err) {
        // ignore abort
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        notifyError("Unable to fetch individual data.");
      } finally {
        setLoading(false);
      }
    },
    [teamId, startDate, endDate, user.token, notifyError]
  );

  // ✅ Effect only triggers fetch, no more dedupe here (fix loop)
  useEffect(() => {
    if (!isRangeComplete) return;

    if (section === "Team") {
      fetchTeamData();
      return;
    }

    if (section === "Individual" && selectedUser?.username) {
      fetchIndividualData(selectedUser.username);
    }
  }, [section, isRangeComplete, fetchTeamData, fetchIndividualData, selectedUser?.username]);

  const toggleSection = () => {
    setSection((s) => (s === "Team" ? "Individual" : "Team"));
  };

  // =====================
  // Active data
  // =====================
  const active = section === "Team" ? summary : individualSummary;

  const targeted = active?.salesGapSummary?.targetAmount || 0;
  const actual = active?.salesGapSummary?.actualSalesAmount || 0;
  const achieved = Number(active?.salesGapSummary?.percentageAchieved || 0);
  const gap = (active?.salesGapSummary?.salesGap || 0) * -1;

  const actualTone = actual >= targeted ? "text-success" : "text-danger";
  const gapTone = achieved >= 95 ? "text-success" : "text-danger";

  // =====================
  // Build countByDate series
  // =====================
  const series = useMemo(() => {
    const dateList = getDateRangeList(startDate, endDate);

    const sumUsers = (countByDateUsers, d) => {
      return Object.values(countByDateUsers || {}).reduce((sum, userMap) => sum + (userMap?.[d] || 0), 0);
    };

    const teamMode = section === "Team";

    const getSalesMetricTeam = (metric, d) => {
      return Object.values(active?.salesGapSummary?.countByDate || {}).reduce((sum, userMap) => {
        return sum + (userMap?.[d]?.[metric] || 0);
      }, 0);
    };

    const getSalesMetricIndividual = (metric, d) => {
      return active?.salesGapSummary?.countByDate?.[d]?.[metric] || 0;
    };

    return {
      dateList,
      leads: (d) => (teamMode ? sumUsers(active?.totalLeadsAdded?.countByDate, d) : active?.totalLeadsAdded?.countByDate?.[d] || 0),
      pres: (d) => (teamMode ? sumUsers(active?.totalPresentations?.countByDate, d) : active?.totalPresentations?.countByDate?.[d] || 0),
      fu: (d) => (teamMode ? sumUsers(active?.followUpScheduled?.countByDate, d) : active?.followUpScheduled?.countByDate?.[d] || 0),
      closed: (d) => {
        const map = active?.finalStatus?.Closed?.countByDate;
        return teamMode ? sumUsers(map, d) : map?.[d] || 0;
      },
      rejected: (d) => {
        const map = active?.finalStatus?.Rejected?.countByDate;
        return teamMode ? sumUsers(map, d) : map?.[d] || 0;
      },
      booking: (d) => {
        const map = active?.finalStatus?.Booking?.countByDate;
        return teamMode ? sumUsers(map, d) : map?.[d] || 0;
      },
      targetAmount: (d) => (teamMode ? getSalesMetricTeam("targetAmount", d) : getSalesMetricIndividual("targetAmount", d)),
      actualSalesAmount: (d) => (teamMode ? getSalesMetricTeam("actualSalesAmount", d) : getSalesMetricIndividual("actualSalesAmount", d)),
      salesGapRow: (d) => {
        const val = teamMode ? getSalesMetricTeam("salesGap", d) : getSalesMetricIndividual("salesGap", d);
        return (val || 0) * -1;
      },
    };
  }, [active, section, startDate, endDate]);

  // =====================
  // Excel exports
  // =====================
  const exportToExcelTeam = async (data, start, end) => {
    const startYmd = toYMD(start);
    const endYmd = toYMD(end);

    const workbook = new ExcelJS.Workbook();

    const summarySheet = workbook.addWorksheet("Summary");
    summarySheet.addRow(["Indicator", "Total", "Details"]);
    summarySheet.addRow(["Total Leads Added", data.totalLeadsAdded.total, ""]);
    summarySheet.addRow(["Total Presentations", data.totalPresentations.total, ""]);
    summarySheet.addRow(["Follow-ups Done", data.followUpScheduled.total, ""]);

    ["Booking", "Closed", "Rejected", "Follow Up"].forEach((status) => {
      const stat = data.finalStatus?.[status] || {};
      summarySheet.addRow([`${status}`, stat.total || 0, ""]);
    });

    summarySheet.addRow(["Avg Follow Up / Lead", data.avgFollowUpPerLead.total || 0, ""]);
    summarySheet.addRow(["Avg Follow Up / Closed Lead", data.followUpPerCloseRatio.total || 0, ""]);

    summarySheet.addRow([
      "Follow Up ➝ Closed Rate",
      `${data.followUpToClosedRate.total || 0}%`,
      `Follow Up: ${data.followUpToClosedRate.totalFollowUpLeads || 0}, Closed: ${data.followUpToClosedRate.totalClosedLeads || 0}`,
    ]);
    summarySheet.addRow([
      "Follow Up ➝ Rejected Rate",
      `${data.followUpToRejectedRate.total || 0}%`,
      `Follow Up: ${data.followUpToRejectedRate.totalFollowUpLeads || 0}, Rejected: ${data.followUpToRejectedRate.totalRejectedLeads || 0}`,
    ]);
    summarySheet.addRow([
      "Follow Up ➝ Booking Rate",
      `${data.followUpToBookingRate.total || 0}%`,
      `Follow Up: ${data.followUpToBookingRate.totalFollowUpLeads || 0}, Booking: ${data.followUpToBookingRate.totalBookingLeads || 0}`,
    ]);
    summarySheet.addRow([
      "Booking ➝ Closed Rate",
      `${data.bookingToClosedRate.total || 0}%`,
      `Booking: ${data.bookingToClosedRate.totalBookingLeads || 0}, Closed: ${data.bookingToClosedRate.totalClosedFromBooking || 0}`,
    ]);
    summarySheet.addRow([
      "Booking ➝ Rejected Rate",
      `${data.bookingToRejectedRate.total || 0}%`,
      `Booking: ${data.bookingToRejectedRate.totalBookingLeads || 0}, Rejected: ${data.bookingToRejectedRate.totalRejectedFromBooking || 0}`,
    ]);

    summarySheet.addRow(["Total Targeted Sales", data.salesGapSummary.targetAmount || 0, ""]);
    summarySheet.addRow(["Total Actual Sales", data.salesGapSummary.actualSalesAmount || 0, ""]);
    summarySheet.addRow(["Total Sales Gap", (data.salesGapSummary.salesGap || 0) * -1, ""]);

    const countSheet = workbook.addWorksheet("Summary By Date");
    countSheet.addRow([
      "Date",
      "Leads Added",
      "Presentation",
      "Follow-ups Done",
      "Closed",
      "Rejected",
      "Booking",
      "Targeted Sales",
      "Actual Sales",
      "Sales Gap",
    ]);

    const dateList = getDateRangeList(startYmd, endYmd);

    const sumUsers = (countByDateUsers, date) => {
      return Object.values(countByDateUsers || {}).reduce((sum, userMap) => sum + (userMap?.[date] || 0), 0);
    };

    const getSalesMetric = (metric, date) => {
      return Object.values(data?.salesGapSummary?.countByDate || {}).reduce((sum, userMap) => sum + (userMap?.[date]?.[metric] || 0), 0);
    };

    dateList.forEach((date) => {
      countSheet.addRow([
        date,
        sumUsers(data.totalLeadsAdded.countByDate, date),
        sumUsers(data.totalPresentations.countByDate, date),
        sumUsers(data.followUpScheduled.countByDate, date),
        sumUsers(data.finalStatus.Closed.countByDate, date),
        sumUsers(data.finalStatus.Rejected.countByDate, date),
        sumUsers(data.finalStatus.Booking.countByDate, date),
        getSalesMetric("targetAmount", date),
        getSalesMetric("actualSalesAmount", date),
        getSalesMetric("salesGap", date) * -1,
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Msmart Team Summary (${user.username}) ${startYmd} to ${endYmd}.xlsx`);
  };

  const exportToExcelIndividual = async (data, start, end) => {
    const startYmd = toYMD(start);
    const endYmd = toYMD(end);

    const workbook = new ExcelJS.Workbook();

    const summarySheet = workbook.addWorksheet("Summary");
    summarySheet.addRow(["Indicator", "Total", "Details"]);
    summarySheet.addRow(["Total Leads Added", data.totalLeadsAdded.total, ""]);
    summarySheet.addRow(["Total Presentations", data.totalPresentations.total, ""]);
    summarySheet.addRow(["Follow-ups Done", data.followUpScheduled.total, ""]);

    ["Booking", "Closed", "Rejected", "Follow Up"].forEach((status) => {
      const stat = data.finalStatus?.[status] || {};
      summarySheet.addRow([`${status}`, stat.total || 0, ""]);
    });

    summarySheet.addRow(["Avg Follow Up / Lead", data.avgFollowUpPerLead.total || 0, ""]);
    summarySheet.addRow(["Avg Follow Up / Closed Lead", data.followUpPerCloseRatio.total || 0, ""]);

    summarySheet.addRow([
      "Follow Up ➝ Closed Rate",
      `${data.followUpToClosedRate.total || 0}%`,
      `Follow Up: ${data.followUpToClosedRate.totalFollowUpLeads || 0}, Closed: ${data.followUpToClosedRate.totalClosedLeads || 0}`,
    ]);
    summarySheet.addRow([
      "Follow Up ➝ Rejected Rate",
      `${data.followUpToRejectedRate.total || 0}%`,
      `Follow Up: ${data.followUpToRejectedRate.totalFollowUpLeads || 0}, Rejected: ${data.followUpToRejectedRate.totalRejectedLeads || 0}`,
    ]);
    summarySheet.addRow([
      "Follow Up ➝ Booking Rate",
      `${data.followUpToBookingRate.total || 0}%`,
      `Follow Up: ${data.followUpToBookingRate.totalFollowUpLeads || 0}, Booking: ${data.followUpToBookingRate.totalBookingLeads || 0}`,
    ]);
    summarySheet.addRow([
      "Booking ➝ Closed Rate",
      `${data.bookingToClosedRate.total || 0}%`,
      `Booking: ${data.bookingToClosedRate.totalBookingLeads || 0}, Closed: ${data.bookingToClosedRate.totalClosedFromBooking || 0}`,
    ]);
    summarySheet.addRow([
      "Booking ➝ Rejected Rate",
      `${data.bookingToRejectedRate.total || 0}%`,
      `Booking: ${data.bookingToRejectedRate.totalBookingLeads || 0}, Rejected: ${data.bookingToRejectedRate.totalRejectedFromBooking || 0}`,
    ]);

    summarySheet.addRow(["Total Targeted Sales", data.salesGapSummary.targetAmount || 0, ""]);
    summarySheet.addRow(["Total Actual Sales", data.salesGapSummary.actualSalesAmount || 0, ""]);
    summarySheet.addRow(["Total Sales Gap", (data.salesGapSummary.salesGap || 0) * -1, ""]);

    const countSheet = workbook.addWorksheet("Summary By Date");
    countSheet.addRow([
      "Date",
      "Leads Added",
      "Presentation",
      "Follow-ups Done",
      "Closed",
      "Rejected",
      "Booking",
      "Targeted Sales",
      "Actual Sales",
      "Sales Gap",
    ]);

    const dateList = getDateRangeList(startYmd, endYmd);
    dateList.forEach((date) => {
      countSheet.addRow([
        date,
        data.totalLeadsAdded.countByDate?.[date] || 0,
        data.totalPresentations.countByDate?.[date] || 0,
        data.followUpScheduled.countByDate?.[date] || 0,
        data.finalStatus.Closed.countByDate?.[date] || 0,
        data.finalStatus.Rejected.countByDate?.[date] || 0,
        data.finalStatus.Booking.countByDate?.[date] || 0,
        data.salesGapSummary.countByDate?.[date]?.targetAmount || 0,
        data.salesGapSummary.countByDate?.[date]?.actualSalesAmount || 0,
        (data.salesGapSummary.countByDate?.[date]?.salesGap || 0) * -1,
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const who = selectedUser?.label || "User";
    saveAs(blob, `Msmart Individual Summary (${who}) ${startYmd} to ${endYmd}.xlsx`);
  };

  // =====================
  // KPI modal helpers (countByDate for sales)
  // =====================
  const salesTargetByDate = useMemo(() => {
    const obj = {};
    const src = active?.salesGapSummary?.countByDate || {};
    if (section === "Individual") {
      Object.keys(src).forEach((d) => (obj[d] = src?.[d]?.targetAmount || 0));
    } else {
      series.dateList.forEach((d) => (obj[d] = series.targetAmount(d)));
    }
    return obj;
  }, [active, section, series]);

  const salesActualByDate = useMemo(() => {
    const obj = {};
    if (section === "Individual") {
      const src = active?.salesGapSummary?.countByDate || {};
      Object.keys(src).forEach((d) => (obj[d] = src?.[d]?.actualSalesAmount || 0));
    } else {
      series.dateList.forEach((d) => (obj[d] = series.actualSalesAmount(d)));
    }
    return obj;
  }, [active, section, series]);

  const salesGapByDate = useMemo(() => {
    const obj = {};
    if (section === "Individual") {
      const src = active?.salesGapSummary?.countByDate || {};
      Object.keys(src).forEach((d) => (obj[d] = (src?.[d]?.salesGap || 0) * -1));
    } else {
      series.dateList.forEach((d) => (obj[d] = series.salesGapRow(d)));
    }
    return obj;
  }, [active, section, series]);

  // =====================
  // Render
  // =====================
  return (
    <div className="ss-page">
      <div className="container py-6">
        <div className="row justify-content-center text-start">
          <div className="col-lg-12">
            <h1 className="header-title mb-1">M-Smart</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
              Sales Activity Management.
            </div>
          </div>
        </div>

        {/* Top nav (Team / Personal) */}
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="msmart-segment my-4">
              <NavLink
                to="#"
                className={({ isActive }) => `msmart-seg-link ${isActive ? "active" : ""}`}
              >
                Team
              </NavLink>

              <NavLink
                to={`/msmart/manager/database/${teamId}`}
                className={({ isActive }) => `msmart-seg-link ${isActive ? "active" : ""}`}
              >
                Personal
              </NavLink>
            </div>
          </div>
        </div>

        <div className="card text-center mt-4">
          {/* Tabs */}
          <div className="card-header text-start">
            <ul className="nav nav-tabs card-header-tabs flex-wrap">
              <SummaryNavItems tab={tab} onChange={setTab} />
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/manage/${teamId}`}>
                  Manage
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/activity/${teamId}`}>
                  Activities
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/followup/${teamId}`}>
                  Follow Up
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/leads/${teamId}`}>
                  Leads
                </Link>
              </li>
            </ul>
          </div>

          <div className="card-body">
            {/* Gate loader */}
            {access.error ? (
            <ErrorState title="Unable to verify access" message={access.error} className="py-2" />
          ) : view === null ? (
            <SummaryBodySkeleton />
          ) : view === false ? (
              <div className="row justify-content-center mt-3">
                <div className="alert alert-danger text-center" role="alert">
                  You do not have permission to access this team M-Smart page
                </div>
              </div>
            ) : (
              <>
                {/* Section toggle */}
                <div className="row justify-content-center">
                  <div className="col-md-3">
                    <nav className="nav nav-pills nav-justified my-3" style={{ border: "1px solid #e9ecef", borderRadius: 40 }}>
                      {section === "Team" ? (
                        <>
                          <button type="button" className="nav-link" style={{ border: "1px solid #0d6efd", borderRadius: 40, color: "#0d6efd" }} disabled>
                            Team
                          </button>
                          <button type="button" className="nav-link" onClick={toggleSection} style={{ color: "grey" }}>
                            Individual
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="nav-link" onClick={toggleSection} style={{ color: "grey" }}>
                            Team
                          </button>
                          <button type="button" className="nav-link" style={{ border: "1px solid #0d6efd", borderRadius: 40, color: "#0d6efd" }} disabled>
                            Individual
                          </button>
                        </>
                      )}
                    </nav>
                  </div>
                </div>

                {/* Sticky Filter Bar */}
                <div className="position-sticky" style={{ top: 74, zIndex: 5 }}>
                  <div className="card p-3 mb-3">
                    <div className="row g-2 align-items-end">
                      {/* Row 1: Date + Select */}
                      <div className={section === "Individual" ? "col-12 col-lg-6" : "col-12 col-lg-8"}>
                        <div className="form-floating">
                        <Flatpickr
                            id="filterDate"
                            className="form-control"
                            placeholder="Select date range"
                            value={draftRange}
                            options={{ mode:"range", dateFormat:"d-m-Y", disableMobile:true }}
                            onChange={(dates) => setDraftRange(dates)}
                            onClose={(dates) => {
                              if (!dates?.length) return;
                              const s = dates[0];
                              const e = dates[1] || dates[0];
                              setAppliedRange([s, e]); // ✅ confirm apply
                            }}
                          />
                          <label htmlFor="filterDate">Date Range</label>
                        </div>
                        <RangePresets
                          disabled={loading}
                          onPick={(r) => { setDraftRange(r); setAppliedRange(r); }}
                        />
                      </div>

                      {section === "Individual" ? (
                        <div className="col-12 col-lg-6 text-start">
                          <label className={`form-label mb-1 ${selectedUser ? "" : "text-muted"}`}>
                            Select user
                          </label>
                          <Select
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
                                boxShadow: state.isFocused ? "0 0 0 0 rgba(13, 110, 253, 0.25)" : "none",
                                "&:hover": {
                                  borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
                                },
                                minHeight: 38,
                                borderRadius: "0.375rem",
                              }),
                            }}
                            classNamePrefix="react-select"
                            options={groupedOptions}
                            onChange={(e) => {
                              if (!e) return;
                              setSelectedUser({ username: e.value, label: e.label });
                            }}
                            isSearchable
                            placeholder={isRangeComplete ? "Search name..." : "Pick date range first"}
                            isDisabled={!isRangeComplete}
                            value={selectedUser ? { value: selectedUser.username, label: selectedUser.label } : null}
                          />
                        </div>
                      ) : null}

                      {/* Row 2: Buttons */}
                      <div className={section === "Individual" ? "col-12" : "col-12 col-lg-4"}>
                        <div className="d-grid gap-2 mt-4">
                          <button
                            className="btn btn-dark w-100"
                            type="button"
                            onClick={() => {
                              const start = safeStart;
                              const end = safeEnd;
                              if (section === "Team") {
                                exportToExcelTeam(summary, start, end);
                              } else {
                                exportToExcelIndividual(individualSummary, start, end);
                              }
                            }}
                            disabled={loading}
                          >
                            <i className="bi bi-download me-2"></i>
                            Export
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="mt-3"><SummaryBodySkeleton withDateBar={false} /></div>
                ) : (
                  <div className="container-fluid p-0">
                    {/* Title */}
                    <div className="row my-3 text-center my-5 msm-section-title">
                      <div className="col-12">
                        <h3 className="mb-0">{section === "Team" ? "Team Summary" : "Individual Summary"}</h3>
                        {section === "Individual" && selectedUser ? <div className="fw-bold">{selectedUser.label}</div> : null}
                        <small className="text-muted">{rangeLabel}</small>
                      </div>
                    </div>

                    {tab === "analysis" ? (
                      <SummaryCharts
                        summary={active}
                        start={safeStart}
                        end={safeEnd}
                        showLeaderboard={section === "Team"}
                        nameOf={(u) => memberName[u]}
                        onSelectMember={openMember}
                      />
                    ) : (
                    <>
                    {/* KPI grid */}
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-cash-coin"
                          title="Targeted Sales"
                          value={money(targeted)}
                          onClick={() =>
                            openKpiModal({
                              title: "Targeted Sales",
                              subtitle: rangeLabel,
                              icon: "bi-cash-coin",
                              tone: "text-dark",
                              total: money(targeted),
                              extraRows: [{ label: "Avg / day", value: money(targeted / daysInRange) }],
                              countByDate: salesTargetByDate,
                              isMoney: true,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-cash-coin"
                          title="Actual Sales"
                          value={money(actual)}
                          tone={actualTone}
                          sub={targeted ? `Achieved: ${achieved}%` : null}
                          onClick={() =>
                            openKpiModal({
                              title: "Actual Sales",
                              subtitle: rangeLabel,
                              icon: "bi-cash-coin",
                              tone: actualTone,
                              total: money(actual),
                              extraRows: [
                                { label: "Achieved", value: `${achieved}%` },
                                { label: "Avg / day", value: money(actual / daysInRange) },
                              ],
                              countByDate: salesActualByDate,
                              isMoney: true,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-graph-up-arrow"
                          title="Sales Gap"
                          value={money(gap)}
                          tone={gapTone}
                          onClick={() =>
                            openKpiModal({
                              title: "Sales Gap",
                              subtitle: rangeLabel,
                              icon: "bi-graph-up-arrow",
                              tone: gapTone,
                              total: money(gap),
                              extraRows: [{ label: "Avg / day", value: money(gap / daysInRange) }],
                              countByDate: salesGapByDate,
                              isMoney: true,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-cloud-plus-fill"
                          title="Database Uploaded"
                          value={active?.totalLeadsAdded?.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Database Uploaded",
                              subtitle: rangeLabel,
                              icon: "bi-cloud-plus-fill",
                              tone: "text-dark",
                              total: active?.totalLeadsAdded?.total || 0,
                              extraRows: [{ label: "Avg / day", value: ((active?.totalLeadsAdded?.total || 0) / daysInRange).toFixed(2) }],
                              countByDate: section === "Team" ? null : active?.totalLeadsAdded?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-easel2-fill"
                          title="Leads Presented"
                          value={active?.totalPresentations?.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Leads Presented",
                              subtitle: rangeLabel,
                              icon: "bi-easel2-fill",
                              tone: "text-dark",
                              total: active?.totalPresentations?.total || 0,
                              extraRows: [{ label: "Avg / day", value: ((active?.totalPresentations?.total || 0) / daysInRange).toFixed(2) }],
                              countByDate: section === "Team" ? null : active?.totalPresentations?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-person"
                          title="Follow-ups Done"
                          value={active?.followUpScheduled?.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Follow Up",
                              subtitle: rangeLabel,
                              icon: "bi-person",
                              tone: "text-dark",
                              total: active?.followUpScheduled?.total || 0,
                              extraRows: [{ label: "Avg / day", value: ((active?.followUpScheduled?.total || 0) / daysInRange).toFixed(2) }],
                              countByDate: section === "Team" ? null : active?.followUpScheduled?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-clipboard2-check"
                          title={active?.finalStatus?.Repeat?.total ? `Closed (+${active.finalStatus.Repeat.total} repeat)` : "Closed"}
                          value={active?.finalStatus?.Closed?.total || 0}
                          tone="text-success"
                          onClick={() =>
                            openKpiModal({
                              title: "Closed",
                              subtitle: rangeLabel,
                              icon: "bi-clipboard2-check",
                              tone: "text-success",
                              total: active?.finalStatus?.Closed?.total || 0,
                              extraRows: [{ label: "Avg / day", value: ((active?.finalStatus?.Closed?.total || 0) / daysInRange).toFixed(2) }],
                              countByDate: section === "Team" ? null : active?.finalStatus?.Closed?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-clipboard2-plus"
                          title="Booked"
                          value={active?.finalStatus?.Booking?.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Booked",
                              subtitle: rangeLabel,
                              icon: "bi-clipboard2-plus",
                              tone: "text-dark",
                              total: active?.finalStatus?.Booking?.total || 0,
                              extraRows: [{ label: "Avg / day", value: ((active?.finalStatus?.Booking?.total || 0) / daysInRange).toFixed(2) }],
                              countByDate: section === "Team" ? null : active?.finalStatus?.Booking?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-clipboard2-x"
                          title="Rejected"
                          value={active?.finalStatus?.Rejected?.total || 0}
                          tone="text-danger"
                          onClick={() =>
                            openKpiModal({
                              title: "Rejected",
                              subtitle: rangeLabel,
                              icon: "bi-clipboard2-x",
                              tone: "text-danger",
                              total: active?.finalStatus?.Rejected?.total || 0,
                              extraRows: [{ label: "Avg / day", value: ((active?.finalStatus?.Rejected?.total || 0) / daysInRange).toFixed(2) }],
                              countByDate: section === "Team" ? null : active?.finalStatus?.Rejected?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>
                    </div>

                    {/* Metrics panels */}
                    <div className="row g-3 mb-3">
                      <div className="col-12">
                        <SectionCard title="Follow Up Rates">
                          <MetricRow label="Follow Up ➝ Booking" value={active?.followUpToBookingRate?.total || 0} suffix="%" />
                          <MetricRow label="Follow Up ➝ Closed" value={active?.followUpToClosedRate?.total || 0} suffix="%" />
                          <MetricRow label="Follow Up ➝ Rejected" value={active?.followUpToRejectedRate?.total || 0} suffix="%" />
                        </SectionCard>
                      </div>

                      <div className="col-12 col-md-6">
                        <SectionCard title="Average Follow Up">
                          <MetricRow label="Per Lead" value={active?.avgFollowUpPerLead?.total || 0} />
                          <MetricRow label="Per Closed Lead" value={active?.followUpPerCloseRatio?.total || 0} />
                        </SectionCard>
                      </div>

                      <div className="col-12 col-md-6">
                        <SectionCard title="Booking Rates">
                          <MetricRow label="Booking ➝ Closed" value={active?.bookingToClosedRate?.total || 0} suffix="%" />
                          <MetricRow label="Booking ➝ Rejected" value={active?.bookingToRejectedRate?.total || 0} suffix="%" />
                        </SectionCard>
                      </div>
                    </div>

                    {/* Summary by Date */}
                    <div className="row g-3">
                      <div className="col-12">
                        <SectionCard title="Summary by Date" right={<span className="badge bg-light text-dark">{series.dateList.length} days</span>}>
                          <div className="table-responsive mt-2" style={{ fontSize: "0.85rem", whiteSpace: "nowrap", maxHeight: 360, overflow: "auto" }}>
                            <table className="table table-bordered table-hover table-sm align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th>Leads Added</th>
                                  <th>Presentation</th>
                                  <th>Follow-ups Done</th>
                                  <th>Closed</th>
                                  <th>Rejected</th>
                                  <th>Booking</th>
                                  <th>Targeted Sales</th>
                                  <th>Actual Sales</th>
                                  <th>Sales Gap</th>
                                </tr>
                              </thead>
                              <tbody>
                                {series.dateList.map((d) => {
                                  const leads = series.leads(d);
                                  const pres = series.pres(d);
                                  const fu = series.fu(d);
                                  const closed = series.closed(d);
                                  const rej = series.rejected(d);
                                  const book = series.booking(d);

                                  const t = series.targetAmount(d);
                                  const a = series.actualSalesAmount(d);
                                  const g = series.salesGapRow(d);

                                  return (
                                    <tr key={d}>
                                      <td className="fw-semibold">{d}</td>
                                      <td className={leads ? "" : "text-danger"}>{leads}</td>
                                      <td className={pres ? "" : "text-danger"}>{pres}</td>
                                      <td className={fu ? "" : "text-danger"}>{fu}</td>
                                      <td className={closed ? "text-success fw-bold" : "text-danger"}>{closed}</td>
                                      <td className={rej ? "text-danger fw-bold" : "text-danger"}>{rej}</td>
                                      <td className={book ? "fw-bold" : "text-danger"}>{book}</td>
                                      <td className={t === 0 ? "text-danger" : ""}>{money(t)}</td>
                                      <td className={a >= t ? "text-success" : "text-danger"}>{money(a)}</td>
                                      <td className={g < 0 ? "text-danger" : g > 0 ? "text-success" : ""}>{money(g)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </SectionCard>
                      </div>
                    </div>
                    </>
                    )}
                  </div>
                )}

                {/* KPI Detail Modal */}
                <div className={`modal fade ${kpiModal.open ? "show d-block" : ""}`} tabIndex="-1" role="dialog">
                  <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <div className="d-flex align-items-center gap-2">
                          <div className="ss-kpi-icon" style={{ width: 40, height: 40 }}>
                            <i className={`bi ${kpiModal.icon}`} />
                          </div>
                          <div>
                            <div className="modal-title">{kpiModal.title}</div>
                            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                              {kpiModal.subtitle}
                            </div>
                          </div>
                        </div>
                        <button type="button" className="btn-close" onClick={closeKpiModal} />
                      </div>

                      <div className="modal-body">
                        <div>
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            Total
                          </div>
                          <div className={`fw-bold ${kpiModal.tone}`} style={{ fontSize: "1.4rem" }}>
                            {kpiModal.total}
                          </div>
                        </div>

                        {kpiModal.extraRows?.length ? (
                          <div className="mt-3">
                            {kpiModal.extraRows.map((r, idx) => (
                              <div key={idx} className="d-flex justify-content-between py-2 border-top">
                                <div className="text-muted">{r.label}</div>
                                <div className="fw-bold">{r.value}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {kpiModal.countByDate ? (
                          <div className="mt-3">
                            <div className="fw-bold mb-2">Top Days</div>
                            {calcTopDays(kpiModal.countByDate, 3).length ? (
                              <div>
                                {calcTopDays(kpiModal.countByDate, 3).map(([d, v]) => (
                                  <div key={d} className="d-flex justify-content-between py-2 border-top">
                                    <div className="text-muted">{d}</div>
                                    <div className="fw-bold">{kpiModal.isMoney ? money(v) : v}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                                No activity in this range.
                              </div>
                            )}
                          </div>
                        ) : null}

                        <div className="d-grid mt-3">
                          <button className="btn btn-dark" onClick={closeKpiModal}>
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {kpiModal.open ? <div className="modal-backdrop fade show" onClick={closeKpiModal} /> : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
